import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import {
  useFloating,
  useRole,
  useClick,
  useInteractions,
  useDismiss,
  flip,
  offset,
  shift,
  FloatingPortal,
  FloatingOverlay,
} from '@floating-ui/react';

type CalendarDay = {
  day: number;
  isOtherMonth: boolean;
};

type DateTimePickerProps = {
  mode?: 'date' | 'datetime';
  changeInput: (value: any) => any;
  valueInput?: any;
  inputStyle?: React.CSSProperties;
  minuteStep?: number;
};

export default function DateTimePicker({
  mode = 'datetime',
  changeInput,
  valueInput,
  inputStyle,
  minuteStep,
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(4), flip(), shift()],
    strategy: 'fixed',
  });
  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    role,
    dismiss,
  ]);

  const handleDateTimeSelected = ({
    date,
    time,
  }: {
    date: string;
    time: string;
  }) => {
    const output = mode === 'date' ? date : `${date} ${time}`;
    changeInput(new Date(output).toISOString());
    setIsOpen(false);
  };

  const value = valueInput
    ? moment(valueInput)
        .local()
        .format(mode === 'datetime' ? 'YYYY/MM/DD HH:mm' : 'YYYY/MM/DD')
    : '';

  return (
    <>
      <DataInput
        type="text"
        value={value}
        readOnly
        style={inputStyle}
        ref={refs.setReference}
        {...getReferenceProps()}
      />
      {isOpen && (
        <FloatingPortal>
          <FloatingOverlay
            lockScroll
            style={{ background: 'rgba(0, 0, 0, 0.3)' }}
          />
          <DateTimeRef
            mode={mode}
            onDateSelected={handleDateTimeSelected}
            style={{
              ...floatingStyles,
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            ref={refs.setFloating}
            initialValue={value}
            minuteStep={minuteStep}
            {...getFloatingProps()}
          />
        </FloatingPortal>
      )}
    </>
  );
}

type DateTimeProps = React.HTMLAttributes<HTMLDivElement> & {
  mode?: 'date' | 'datetime';
  style?: React.CSSProperties;
  onDateSelected: (params: { date: string; time: string }) => void;
  initialValue?: string;
  minuteStep?: number;
};

const DateTimeRef = React.forwardRef<HTMLDivElement, DateTimeProps>(DateTime);

function DateTime(
  {
    mode = 'datetime',
    style,
    onDateSelected,
    initialValue,
    minuteStep = 1,
    ...rest
  }: DateTimeProps,
  ref: React.Ref<HTMLDivElement>
) {
  const initialMoment = initialValue
    ? moment(
        initialValue,
        ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm', 'YYYY/MM/DD', 'YYYY/MM/DD HH:mm'],
        true
      )
    : moment();

  const [currentDate, setCurrentDate] = useState(() => initialMoment.clone());
  const [selectedDate, setSelectedDate] = useState(() => initialMoment.clone());
  const [selectedHour, setSelectedHour] = useState(initialMoment.format('HH'));
  const [selectedMinute, setSelectedMinute] = useState(() => {
    const raw = initialMoment.minute();
    const snapped = (Math.round(raw / minuteStep) * minuteStep) % 60;
    return String(snapped).padStart(2, '0');
  });

  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  const weekDays = ['日', '月', '火', '水', '木', '金', '土'];
  const hours = Array.from({ length: 24 }, (_, i) =>
    String(i).padStart(2, '0')
  );
  const minutes = Array.from({ length: 60 }, (_, i) => i)
    .filter((m) => m % minuteStep === 0)
    .map((m) => String(m).padStart(2, '0'));

  useEffect(() => {
    if (hourListRef.current) {
      const el = hourListRef.current.querySelector(
        `[data-hour="${selectedHour}"]`
      );
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (minuteListRef.current) {
      const el = minuteListRef.current.querySelector(
        `[data-minute="${selectedMinute}"]`
      );
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedHour, selectedMinute]);

  const generateCalendarDays = useMemo<CalendarDay[]>(() => {
    const daysInMonth = currentDate.daysInMonth();
    const firstDayOfMonth = currentDate.clone().startOf('month').day();
    const prevMonthDays = currentDate
      .clone()
      .subtract(1, 'month')
      .daysInMonth();

    const days: CalendarDay[] = [];

    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      days.push({ day: prevMonthDays - i, isOtherMonth: true });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isOtherMonth: false });
    }
    const remaining = 35 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ day: i, isOtherMonth: true });
    }

    return days;
  }, [currentDate]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate((prev) =>
      direction === 'prev'
        ? prev.clone().subtract(1, 'month')
        : prev.clone().add(1, 'month')
    );
  };

  const navigateYear = (direction: 'prev' | 'next') => {
    setCurrentDate((prev) =>
      direction === 'prev'
        ? prev.clone().subtract(1, 'year')
        : prev.clone().add(1, 'year')
    );
  };

  const handleDateSelect = (day: number, isOtherMonth: boolean) => {
    if (isOtherMonth) return;
    const newDate = currentDate.clone().date(day);
    if (mode === 'date') {
      handleConfirm(newDate);
      return;
    }
    setSelectedDate(newDate);
  };

  const handleConfirm = (date?: moment.Moment) => {
    const selectedMoment = (date ?? selectedDate)
      .clone()
      .hour(parseInt(selectedHour, 10))
      .minute(parseInt(selectedMinute, 10))
      .second(0);

    if (mode === 'date') {
      onDateSelected({ date: selectedMoment.format('YYYY-MM-DD'), time: '' });
    } else {
      onDateSelected({
        date: selectedMoment.format('YYYY-MM-DD'),
        time: selectedMoment.format('HH:mm'),
      });
    }
  };

  const handleNavigation =
    (
      navigateFn: (direction: 'prev' | 'next') => void,
      direction: 'prev' | 'next'
    ) =>
    (e: React.MouseEvent) => {
      e.preventDefault();
      navigateFn(direction);
    };

  return (
    <Container style={style} ref={ref} {...rest}>
      <InnerContainer>
        <Calendar>
          <Header>
            <NavigationButton onClick={handleNavigation(navigateYear, 'prev')}>
              <DoubleLeftOutlined />
            </NavigationButton>
            <NavigationButton onClick={handleNavigation(navigateMonth, 'prev')}>
              <LeftOutlined />
            </NavigationButton>
            <MonthYear>{`${currentDate.year()}年${
              currentDate.month() + 1
            }月`}</MonthYear>
            <NavigationButton onClick={handleNavigation(navigateMonth, 'next')}>
              <RightOutlined />
            </NavigationButton>
            <NavigationButton onClick={handleNavigation(navigateYear, 'next')}>
              <DoubleRightOutlined />
            </NavigationButton>
          </Header>

          <MonthView>
            {weekDays.map((day) => (
              <WeekDay key={day}>{day}</WeekDay>
            ))}
            {generateCalendarDays.map((day, index) => (
              <Day
                key={index}
                isSelected={
                  !day.isOtherMonth &&
                  day.day === selectedDate.date() &&
                  currentDate.month() === selectedDate.month() &&
                  currentDate.year() === selectedDate.year()
                }
                isOtherMonth={day.isOtherMonth}
                onClick={(e) => {
                  e.preventDefault();
                  handleDateSelect(day.day, day.isOtherMonth);
                }}
              >
                {day.day}
              </Day>
            ))}
          </MonthView>
          <ActionButton
            onClick={(e) => {
              e.preventDefault();
              const today = moment();
              setCurrentDate(today);
              setSelectedDate(today);
            }}
          >
            今日
          </ActionButton>
        </Calendar>

        {mode === 'datetime' && <Separator />}

        {mode === 'datetime' && (
          <TimeContainer>
            <TimeHeader>
              <p>時</p>
              <p>分</p>
            </TimeHeader>
            <TimeColumns>
              <TimeList ref={hourListRef}>
                {hours.map((hour) => (
                  <TimeButton
                    key={hour}
                    isSelected={hour === selectedHour}
                    data-hour={hour}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedHour(hour);
                    }}
                  >
                    {hour}
                  </TimeButton>
                ))}
              </TimeList>
              <TimeList ref={minuteListRef}>
                {minutes.map((minute) => (
                  <TimeButton
                    key={minute}
                    isSelected={minute === selectedMinute}
                    data-minute={minute}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedMinute(minute);
                    }}
                  >
                    {minute}
                  </TimeButton>
                ))}
              </TimeList>
            </TimeColumns>
            <ActionButton
              onClick={(e) => {
                e.preventDefault();
                const now = moment();
                setSelectedHour(now.format('HH'));
                setSelectedMinute(now.format('mm'));
              }}
            >
              現在時刻
            </ActionButton>
          </TimeContainer>
        )}
      </InnerContainer>

      {mode === 'datetime' && (
        <ActionButton
          primary
          onClick={(e) => {
            e.preventDefault();
            handleConfirm();
          }}
        >
          決定する
        </ActionButton>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0px 4px 30px rgba(23, 47, 121, 0.1);
  padding: 24px;
  gap: 24px;
  z-index: 9999;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const MonthYear = styled.h2`
  font-size: 14px;
  font-weight: 700;
  color: #1a2737;
  margin: 0;
`;

const NavigationButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #333;

  &:hover {
    color: #0066ff;
  }
`;

const Calendar = styled.div`
  width: 248px;
`;

const MonthView = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 8px;
  column-gap: 4px;
`;

const WeekDay = styled.div`
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  color: #7c878c;
  width: 32px;
  height: 17px;
`;

const Day = styled.button<{
  isSelected?: boolean;
  isOtherMonth?: boolean;
}>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
  background: ${(props) => (props.isSelected ? '#0175FF' : 'transparent')};
  color: ${(props) => {
    if (props.isSelected) return '#FFFFFF';
    if (props.isOtherMonth) return '#EEF1F2';
    return '#1A2737';
  }};
  cursor: pointer;

  &:hover {
    background: ${(props) => {
      if (props.isSelected) return '#0066ff';
      if (props.isOtherMonth) return 'transparent';
      return '#F0F0F0';
    }};
  }
`;

const TimeHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  border-bottom: 1px solid #e9eff2;
  padding: 4px 0;
  color: #1a2737;

  p {
    margin: 0;
    padding: 0;
  }
`;

const TimeColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: 225px;
`;

const TimeContainer = styled.div`
  width: 96px;
  max-height: 310px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
`;

const TimeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TimeButton = styled.button<{ isSelected?: boolean }>`
  width: 40px;
  height: 24px;
  border: none;
  border-radius: 100px;
  cursor: pointer;
  font-size: 13px;
  align-self: center;
  background: ${(props) => (props.isSelected ? '#0066ff' : 'transparent')};
  color: ${(props) => (props.isSelected ? '#ffffff' : '#333')};

  &:hover {
    background: ${(props) => (props.isSelected ? '#0066ff' : '#f0f0f0')};
  }
`;

const ActionButton = styled.button<{ primary?: boolean }>`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 100px;
  background-color: ${(props) => (props.primary ? '#0175ff' : '#eef1f2')};
  color: ${(props) => (props.primary ? '#ffffff' : '#1a2737')};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 12px;

  &:hover {
    background-color: ${(props) => (props.primary ? '#0066ff' : '#d5dbdd')};
  }
`;

const Separator = styled.div`
  width: 1px;
  background: #e9eff2;
  height: 310px;
  margin: 0 16px;
`;

const DataInput = styled.input`
  flex: 1;
  width: 100%;
  padding: 8px 16px;
  border: none;
  background-color: transparent;
  border-radius: 4px;
  font-size: 14px;
  min-height: 40px;
  height: 100%;
  cursor: pointer;
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: #7c878c;
  }
`;
